import { supabase } from "@/integrations/supabase/client";
import { getBelgianCandidates } from "@/utils/candidate/operations/sampleData";

export interface SeedResult {
	firstJobId: string | null;
	firstMatchId: string | null;
	seeded: boolean;
}

const emptyResult: SeedResult = {
	firstJobId: null,
	firstMatchId: null,
	seeded: false,
};

const pickFirstIds = async (orgId: string): Promise<SeedResult> => {
	const { data: firstJob } = await supabase
		.from("jobs")
		.select("id")
		.eq("organization_uuid", orgId)
		.order("created_at", { ascending: false })
		.limit(1)
		.maybeSingle();

	let firstMatchId: string | null = null;
	if (firstJob?.id) {
		const { data: firstMatch } = await supabase
			.from("matches")
			.select("id")
			.eq("job_id", firstJob.id)
			.order("score", { ascending: false })
			.limit(1)
			.maybeSingle();
		firstMatchId = firstMatch?.id ?? null;
	}

	return {
		firstJobId: firstJob?.id ?? null,
		firstMatchId,
		seeded: false,
	};
};

export const seedSampleDataIfEmpty = async (
	userId: string,
	orgId: string,
): Promise<SeedResult> => {
	if (!userId || !orgId) return emptyResult;

	const { data: existingJobs } = await supabase
		.from("jobs")
		.select("id")
		.eq("organization_uuid", orgId)
		.limit(1);

	if (existingJobs && existingJobs.length > 0)
		return pickFirstIds(orgId);

	const { data: existingCandidates } = await supabase
		.from("candidates")
		.select("id")
		.eq("organization_uuid", orgId)
		.limit(1);

	let candidateIds: string[] = [];

	if (!existingCandidates || existingCandidates.length === 0) {
		const sample = getBelgianCandidates().slice(0, 5).map((c) => ({
			...c,
			organization_uuid: orgId,
		}));
		const { data: inserted, error } = await supabase
			.from("candidates")
			.insert(sample)
			.select("id");
		if (error || !inserted) {
			console.error("[tour seed] candidates failed", error);
			return emptyResult;
		}
		candidateIds = inserted.map((c) => c.id);
	} else {
		const { data: existing } = await supabase
			.from("candidates")
			.select("id")
			.eq("organization_uuid", orgId)
			.limit(5);
		candidateIds = (existing ?? []).map((c) => c.id);
	}

	if (candidateIds.length === 0)
		return emptyResult;

	const { data: company, error: coErr } = await supabase
		.from("companies")
		.insert({
			name: "Acme Inc (sample)",
			organization_uuid: orgId,
			assignee: userId,
		})
		.select("id")
		.single();
	if (coErr || !company) {
		console.error("[tour seed] company failed", coErr);
		return emptyResult;
	}

	const { data: job, error: jobErr } = await supabase
		.from("jobs")
		.insert({
			title: "Senior Full Stack Engineer (sample)",
			company_id: company.id,
			organization_uuid: orgId,
			description:
				"Sample job for the onboarding tour. We're looking for an experienced engineer with strong TypeScript, React, and Node.js skills to join our growing team. Feel free to delete this once you've finished exploring.",
			skills: ["TypeScript", "React", "Node.js", "PostgreSQL"],
			status: "open",
			published: true,
			publishdate: new Date().toISOString(),
		})
		.select("id")
		.single();
	if (jobErr || !job) {
		console.error("[tour seed] job failed", jobErr);
		return emptyResult;
	}

	const stages = ["interview", "screening", "screening", "applied", "applied"];
	const matchRows = candidateIds.map((cid, i) => ({
		candidate_id: cid,
		job_id: job.id,
		score: 92 - i * 5,
		status: stages[i] ?? "applied",
	}));
	const { data: matches, error: matchErr } = await supabase
		.from("matches")
		.insert(matchRows)
		.select("id, score");
	if (matchErr || !matches) {
		console.error("[tour seed] matches failed", matchErr);
		return {
			firstJobId: job.id,
			firstMatchId: null,
			seeded: true,
		};
	}

	const sortedMatches = [...matches].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
	return {
		firstJobId: job.id,
		firstMatchId: sortedMatches[0]?.id ?? null,
		seeded: true,
	};
};
